import { Component, OnInit, ViewChild, ElementRef } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { MatDialog } from "@angular/material";
import { ErrorDialogComponent } from "../error-dialog/error-dialog.component";
import { PhasedRollOut } from "../models/phased-rollout.model";
import { Capability } from "../models/capability.model";
import { Functionality } from "../models/functionality.model";
import { DataService } from "../services/data.service";
import { FormControl, NgForm } from "@angular/forms";
import { Observable, forkJoin } from "rxjs";
import { GraphUser } from "../models/graph-user.model";
import { startWith, map, tap } from "rxjs/operators";
import _ from "lodash";
import { ProjectClass } from "../models/project-class.model";
import { PatiProject } from "../models/pati-project.model";
import { Router, ActivatedRoute } from "@angular/router";
import { ReleaseDetail } from "../models/release-detail.model";
import { Allocation } from "../models/allocation.model";
import { Building } from "../models/building.model";
import { LaborDetail } from "../models/labor-detail.model";
import { ContingencyDetail } from "../models/contingency-detail.model";
import { UploadFile } from "../models/upload-file.model";
import { ConfirmDeleteFormDialogComponent } from "../confirm-delete-form-dialog/confirm-delete-form-dialog.component";
import { parseOperatingUnit } from "../shared/utils/operating-unit.util";

@Component({
  selector: "app-it-project-setup",
  templateUrl: "./it-project-setup.component.html",
  styleUrls: ["./it-project-setup.component.scss"],
})
export class ItProjectSetupComponent implements OnInit {
  distributeCost: any;
  @ViewChild("datepickerInput1", { static: false })
  datepickerInput1: ElementRef;
  @ViewChild("datepickerInput2", { static: false })
  datepickerInput2: ElementRef;
  patiProject: PatiProject;
  validatingPati: boolean;
  patiValidated: boolean;
  invalidPati: boolean;
  projectName: string;
  itProjectClassification: any;
  itProjectClassificationOptions: any[];
  itProjectClass: ProjectClass;
  itProjectClassOptions: ProjectClass[];
  isLighthouseProject: string;
  isLighthouseProjectOptions: string[];
  estimatedStartDate: Date;
  estimatedInServiceDate: Date;
  todayDate: string;
  projectScopeDescription: string;
  primaryContact: GraphUser;
  primaryContactOptions: GraphUser[];
  itSponsor: GraphUser;
  itSponsorOptions: GraphUser[];
  hardwareOrSoftware: string;
  hardwareOrSoftwarePrevious: string;
  hardwareOrSoftwareOptions: string[];
  workOptions: { [key: string]: string[] };
  projectConsistsOf: string[];
  projectConsistsOfOptions: string[];
  projectConsistsOfCheckboxes: boolean[] = [];
  inServiceYear: string;
  softwareBeingUsed: string;
  softwareProjectOverview: string;
  vendorName: string;
  softwareName: string;
  dataConversionActivities: string;
  trainingCosts: string;
  associatedRetirement: string;
  softwareToBeRetired: string;
  retiredInServiceDate: string;
  replacingInterfaces: string;
  howSoftwareReleased: string;
  howSoftwareReleasedOptions: string[];
  phasedRollOuts: PhasedRollOut[];
  capabilities: Capability[];
  functionalities: Functionality[];
  patiProjectOptions: PatiProject[];
  gettingPatiIds: boolean;
  purchasingWarranties: string;
  expectedLife: number;
  agileEffort: string;
  maintenanceContracts: string;
  perpetualLicenses: string;
  contractualRight: string;
  significantPenalty: string;
  isFeasible: string;
  serviceFees: string;
  useWithoutPaying: string;
  currentDate = new Date();
  showErrorsDisclaimer: boolean;
  itSparGuid: string;
  department: any;
  buildingInfo: any;
  releaseDetails: ReleaseDetail[];
  defaultOption: any;
  allocations: Allocation[];
  buildings: Building[];
  laborDetails: LaborDetail[];
  contingencyDetails: ContingencyDetail[];
  additionalComments: string;
  files: UploadFile[];
  additionalApprovers: GraphUser[];
  doaAmounts: number[];
  draftSaved: string;
  draftSavedDate: Date;
  draftSavedDay: string;
  draftSavedTime: string;
  buForStateOptions: string[];
  approverList: any[];
  sparDataLoaded: boolean;
  isRevisionIndicator: boolean;
  formStatus: string;
  displayRevisionNumber: number;
  previousCapitalSubtotal: number;
  previousOMSubtotal: number;
  formOwner: GraphUser;
  omOnly: string;

  primaryContactControl = new FormControl();
  filteredPrimaryContactOptions: Observable<GraphUser[]>;
  lastFilter: string = "";

  itSponsorControl = new FormControl();
  filteredItSponsorOptions: Observable<GraphUser[]>;
  itSponsorLastFilter: string = "";

  patiProjectControl = new FormControl();
  filteredPatiProjectOptions: Observable<PatiProject[]>;
  patiProjectLastFilter: string = "";

  itProjectClassificationControl = new FormControl();
  filteredItProjectClassificationOptions: Observable<any[]>;

  howSoftwareReleasedControl = new FormControl();
  filteredHowSoftwareReleasedOptions: Observable<string[]>;

  isRevision: string;
  formNumber: string;
  firstName: string;
  lastName: string;
  userName: string;
  userEmail: string;
  displayPatiProject: string;
  etsPacAssignment: any;
  userRoles: string[] = [];
  relatedWork: string;

  constructor(
    private http: HttpClient,
    private dialog: MatDialog,
    private dataService: DataService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit() {
    let userData = JSON.parse(sessionStorage.getItem("userInfo"));
    if (userData) {
      let stringArray = userData.account.name.split(",");
      this.lastName = stringArray[0];
      this.firstName = stringArray[1].substring(1);
      this.userName = this.firstName + " " + this.lastName;
      this.userEmail = userData.account.username;
      this.userRoles = (userData.idTokenClaims && userData.idTokenClaims.roles) || [];
    }
    this.sparDataLoaded = false;
    this.isRevisionIndicator = false;
    this.loadIsLighthouseProjectOptionsFile();
    this.loadHowSoftwareReleasedOptionsFile();
    this.loadProjectClassificationOptionsFile();
    this.getPatiID();
    forkJoin([
      this.loadBUForStateFile(),
      this.loadHardwareOrSoftwareOptionsFile(),
      this.loadProjectConsistsOfOptionsFile(),
    ]).subscribe(
      () => this.initializeProjectData(),
      () => {
        // Lookup loaders display their existing error dialogs.
        // Do not initialize the form with incomplete lookup data.
      }
    );
  }

  initializeProjectData() {
    this.isRevision = sessionStorage.getItem("isRevision");
    this.route.queryParams.subscribe((params) => {
      if (params.guid) {
        this.itSparGuid = params.guid;
        if (params.revisionNum) {
          this.displayRevisionNumber = params.revisionNum;
        }
        this.getItSparData(params.guid, false, this.displayRevisionNumber);
      } else if (this.isRevision === "yes") {
        this.isRevisionIndicator = true;
        this.displayRevisionNumber = Number(
          sessionStorage.getItem("displayRevisionNumber")
        );
        this.itSparGuid = JSON.parse(sessionStorage.getItem("itSparGuid"));
        this.previousCapitalSubtotal = Number(
          sessionStorage.getItem("previousCapitalSubtotal")
        );
        this.previousOMSubtotal = Number(
          sessionStorage.getItem("previousOMSubtotal")
        );
        sessionStorage.removeItem("isRevision");
        this.getItSparData(this.itSparGuid, true, this.displayRevisionNumber);
      } else {
        this.loadProjectData();
        this.validatingPati = false;
        this.patiValidated = false;
        this.invalidPati = false;
        this.generateTodaysDate();
        this.filteredPrimaryContactOptions =
          this.primaryContactControl.valueChanges.pipe(
            startWith<string | GraphUser[]>(""),
            map((value) =>
              typeof value === "string" ? value : this.lastFilter
            ),
            map((filter) => this.primaryContactFilter(filter))
          );
        this.filteredItSponsorOptions = this.itSponsorControl.valueChanges.pipe(
          startWith<string | GraphUser[]>(""),
          map((value) => (typeof value === "string" ? value : this.lastFilter)),
          map((filter) => this.itSponsorFilter(filter))
        );
      }
    });
  }

  primaryContactFilter(filter: string): GraphUser[] {
    this.lastFilter = filter;
    if (filter) {
      return this.primaryContactOptions.filter((option) => {
        return true;
      });
    } else {
      return [];
    }
  }

  itSponsorFilter(filter: string): GraphUser[] {
    this.itSponsorLastFilter = filter;
    if (filter) {
      return this.itSponsorOptions.filter((option) => {
        return true;
      });
    } else {
      return [];
    }
  }

  itProjectClassificationFilter(filter: string): any[] {
    this.lastFilter = filter;
    if (filter) {
      return this.itProjectClassificationOptions.filter((option) => {
        return option.ets_proj_classification
          .toLowerCase()
          .includes(filter.toLowerCase());
      });
    } else {
      return this.itProjectClassificationOptions;
    }
  }

  howSoftwareReleasedFilter(filter: string): any[] {
    this.lastFilter = filter;
    if (filter) {
      return this.howSoftwareReleasedOptions.filter((option) => {
        return option.toLowerCase().includes(filter.toLowerCase());
      });
    } else {
      return this.howSoftwareReleasedOptions;
    }
  }

  patiProjectFilter(filter: string): PatiProject[] {
    this.lastFilter = filter;
    if (filter) {
      var tokens = filter.toLowerCase().split(/\s+/).filter(function(t) { return t.length > 0; });
      return this.patiProjectOptions.filter(function(option) {
        var haystack = option.Project_ID.concat(" - ").concat(option.Project_Name).toLowerCase();
        return tokens.every(function(token) { return haystack.includes(token); });
      });
    } else {
      return this.patiProjectOptions;
    }
  }

  displayFnPrimaryContact(value: GraphUser): any | undefined {
    if (!value) {
      return "";
    } else if (value.givenName && value.surname && value.jobTitle) {
      return (
        value.givenName +
        " " +
        value.surname +
        " (" +
        value.mail +
        "), " +
        value.jobTitle
      );
    } else if (value.givenName && value.surname && !value.jobTitle) {
      return value.givenName + " " + value.surname + " (" + value.mail + ")";
    } else if (value.displayName && value.jobTitle) {
      return value.displayName + " (" + value.mail + "), " + value.jobTitle;
    } else if (value.displayName && !value.jobTitle) {
      return value.displayName + " (" + value.mail + ")";
    } else {
      return value;
    }
  }

  displayFnPatiProject(value: PatiProject): string | undefined {
    if (!value) {
      return "";
    } else {
      return value.Project_ID + " - " + value.Project_Name;
    }
  }

  displayFnITSponsor(value: GraphUser): any | undefined {
    if (!value) {
      return "";
    } else if (value.givenName && value.surname && value.jobTitle) {
      return (
        value.givenName +
        " " +
        value.surname +
        " (" +
        value.mail +
        "), " +
        value.jobTitle
      );
    } else if (value.givenName && value.surname && !value.jobTitle) {
      return value.givenName + " " + value.surname + " (" + value.mail + ")";
    } else if (value.displayName && value.jobTitle) {
      return value.displayName + " (" + value.mail + "), " + value.jobTitle;
    } else if (value.displayName && !value.jobTitle) {
      return value.displayName + " (" + value.mail + ")";
    } else {
      return value;
    }
  }

  displayFnItProjectClassification(value: any): string | undefined {
    if (!value) {
      return "";
    } else {
      return value;
    }
  }

  displayFnHardwareOrSoftware(value: any): string | undefined {
    if (!value) {
      return "";
    } else {
      return value;
    }
  }

  displayFnHowSoftwareReleased(value: any): string | undefined {
    if (!value) {
      return "";
    } else {
      return value;
    }
  }

  generateTodaysDate() {
    let today = new Date(new Date().getTime() + 24 * 60 * 60 * 1000);
    this.todayDate =
      today.getFullYear().toString() +
      "-" +
      (today.getMonth() + 1).toString().padStart(2, "0") +
      "-" +
      today.getDate().toString().padStart(2, "0");
  }

  minimumInServiceDate() {
    if (this.estimatedStartDate) {
      return this.estimatedStartDate;
    } else {
      return this.todayDate;
    }
  }

  loadIsLighthouseProjectOptionsFile() {
    this.http.get("assets/is-lighthouse-project-options.json").subscribe(
      (result) => {
        this.isLighthouseProjectOptions = result as string[];
      },
      (error) => {
        console.log("error", error);
        let errorType = "";
        let errorDescription = "";
        if (error.error.message) {
          errorDescription = errorDescription.concat(
            "Load Is Lighthouse Project Options Error - ",
            error.error.message
          );
        } else {
          errorDescription = errorDescription.concat(
            "Load Is Lighthouse Project Options Error"
          );
        }
        errorType = errorType.concat(error.status, " ", error.statusText);
        let dataStructure = {
          errorType: errorType,
          errorDescription: errorDescription,
        };
        this.dialog.open(ErrorDialogComponent, {
          data: dataStructure,
          disableClose: true,
        });
      }
    );
  }

  updateHardwareOrSoftwareOptions() {
    if (this.omOnly.toUpperCase() === "YES") {
      this.hardwareOrSoftwareOptions = this.workOptions["O&M"];
    } else {
      this.hardwareOrSoftwareOptions = this.workOptions["Not O&M"];
    }
  }

  omOnlyChanged() {
    this.updateHardwareOrSoftwareOptions();
    this.relatedWork = "";
    this.relatedWorkChanged();
  }

  relatedWorkChanged() {
    if (!this.relatedWork) {
      this.hardwareOrSoftware = "";
      this.hardwareSoftwareChanged();
      return;
    }
    if (this.omOnly.toUpperCase() === "YES") {
      this.hardwareOrSoftware = "O&M Only";
    } else {
      if (this.relatedWork === "Hardware and Software Both") {
        this.hardwareOrSoftware = "Hardware and Software";
      }
      if (this.relatedWork === "Hardware") {
        this.hardwareOrSoftware = "Hardware Only";
      }
      if (this.relatedWork === "Software") {
        this.hardwareOrSoftware = "Software Only";
      }
    }

    this.hardwareSoftwareChanged();
  }

  hardwareSoftwareChanged() {
    if (
      !(
        (this.hardwareOrSoftwarePrevious === "Hardware Only" &&
          this.hardwareOrSoftware === "Hardware and Software") ||
        (this.hardwareOrSoftwarePrevious === "Hardware and Software" &&
          this.hardwareOrSoftware === "Hardware Only")
      )
    ) {
      this.purchasingWarranties = "";
    }
    if (
      !(
        (this.hardwareOrSoftwarePrevious === "Software Only" &&
          this.hardwareOrSoftware === "Hardware and Software") ||
        (this.hardwareOrSoftwarePrevious === "Hardware and Software" &&
          this.hardwareOrSoftware === "Software Only")
      )
    ) {
      for (let i = 0; i < this.projectConsistsOfCheckboxes.length; i++) {
        this.projectConsistsOfCheckboxes[i] = false;
      }
      this.expectedLife = null;
      this.agileEffort = "";
      this.softwareBeingUsed = "";
      this.vendorName = "";
      this.softwareName = "";
      this.inServiceYear = "";
      this.softwareProjectOverview = "";
      this.dataConversionActivities = "";
      this.trainingCosts = "";
      this.associatedRetirement = "";
      this.softwareToBeRetired = "";
      this.retiredInServiceDate = "";
      this.maintenanceContracts = "";
      this.replacingInterfaces = "";
      this.howSoftwareReleased = "";
      this.perpetualLicenses = "";
      this.contractualRight = "";
      this.significantPenalty = "";
      this.isFeasible = "";
      this.serviceFees = "";
      this.useWithoutPaying = "";
      sessionStorage.setItem(
        "releaseDetails",
        JSON.stringify([new ReleaseDetail()])
      );
      this.releaseDetails = [new ReleaseDetail()];
    }
    this.hardwareOrSoftwarePrevious = this.hardwareOrSoftware;
  }

  loadHardwareOrSoftwareOptionsFile() {
    return this.http.get("assets/hardware-or-software-options.json").pipe(tap(
      (result) => {
        this.workOptions = result as { [key: string]: string[] };
        console.log("workOptions", this.workOptions);
      },
      (error) => {
        console.log("error", error);
        let errorType = "";
        let errorDescription = "";
        if (error.error.message) {
          errorDescription = errorDescription.concat(
            "Load Hardware or Software Options Error - ",
            error.error.message
          );
        } else {
          errorDescription = errorDescription.concat(
            "Load Hardware or Software Options Error"
          );
        }
        errorType = errorType.concat(error.status, " ", error.statusText);
        let dataStructure = {
          errorType: errorType,
          errorDescription: errorDescription,
        };
        this.dialog.open(ErrorDialogComponent, {
          data: dataStructure,
          disableClose: true,
        });
      }
    ));
  }

  loadProjectConsistsOfOptionsFile() {
    return this.http.get("assets/project-consists-of-options.json").pipe(tap(
      (result) => {
        this.projectConsistsOfOptions = result as string[];
        if (!this.projectConsistsOfCheckboxes.length) {
          this.projectConsistsOfCheckboxes = [];
          for (let i = 0; i < this.projectConsistsOfOptions.length; i++) {
            this.projectConsistsOfCheckboxes.push(false);
          }
        }
      },
      (error) => {
        console.log("error", error);
        let errorType = "";
        let errorDescription = "";
        if (error.error.message) {
          errorDescription = errorDescription.concat(
            "Load Project Consists Of Options Error - ",
            error.error.message
          );
        } else {
          errorDescription = errorDescription.concat(
            "Load Project Consists Of Options Error"
          );
        }
        errorType = errorType.concat(error.status, " ", error.statusText);
        let dataStructure = {
          errorType: errorType,
          errorDescription: errorDescription,
        };
        this.dialog.open(ErrorDialogComponent, {
          data: dataStructure,
          disableClose: true,
        });
      }
    ));
  }

  loadHowSoftwareReleasedOptionsFile() {
    this.http.get("assets/how-software-released-options.json").subscribe(
      (result) => {
        this.howSoftwareReleasedOptions = result as string[];
        this.filteredHowSoftwareReleasedOptions =
          this.howSoftwareReleasedControl.valueChanges.pipe(
            startWith<string | string[]>(""),
            map((value) =>
              typeof value === "string" ? value : this.lastFilter
            ),
            map((filter) => this.howSoftwareReleasedFilter(filter))
          );
      },
      (error) => {
        console.log("error", error);
        let errorType = "";
        let errorDescription = "";
        if (error.error.message) {
          errorDescription = errorDescription.concat(
            "How Software Released Options Error - ",
            error.error.message
          );
        } else {
          errorDescription = errorDescription.concat(
            "How Software Released Options Error"
          );
        }
        errorType = errorType.concat(error.status, " ", error.statusText);
        let dataStructure = {
          errorType: errorType,
          errorDescription: errorDescription,
        };
        this.dialog.open(ErrorDialogComponent, {
          data: dataStructure,
          disableClose: true,
        });
      }
    );
  }

  loadProjectClassificationOptionsFile() {
    this.http.get("assets/it-project-classification-options.json").subscribe(
      (result) => {
        this.itProjectClassificationOptions = result as any[];
        this.filteredItProjectClassificationOptions =
          this.itProjectClassificationControl.valueChanges.pipe(
            startWith<string | string[]>(""),
            map((value) =>
              typeof value === "string" ? value : this.lastFilter
            ),
            map((filter) => this.itProjectClassificationFilter(filter))
          );
      },
      (error) => {
        console.log("error", error);
        let errorType = "";
        let errorDescription = "";
        if (error.error.message) {
          errorDescription = errorDescription.concat(
            "Load Project Classification Options Error - ",
            error.error.message
          );
        } else {
          errorDescription = errorDescription.concat(
            "Load Project Classification Options Error"
          );
        }
        errorType = errorType.concat(error.status, " ", error.statusText);
        let dataStructure = {
          errorType: errorType,
          errorDescription: errorDescription,
        };
        this.dialog.open(ErrorDialogComponent, {
          data: dataStructure,
          disableClose: true,
        });
      }
    );
  }

  addRelease() {
    this.phasedRollOuts.push(new PhasedRollOut());
  }

  deleteRelease(index: number) {
    this.phasedRollOuts.splice(index, 1);
  }

  addCapability() {
    this.capabilities.push(new Capability());
  }

  deleteCapability(index: number) {
    this.capabilities.splice(index, 1);
  }

  addFunctionality() {
    this.functionalities.push(new Functionality());
  }

  deleteFunctionality(index: number) {
    this.functionalities.splice(index, 1);
  }

  searchUsersPrimaryContact(searchText: any) {
    this.dataService.searchUsers(searchText).subscribe((result) => {
      this.primaryContactOptions = _.orderBy(result["value"], [
        "surname",
        "givenName",
      ]);
      setTimeout(() => {
        document.getElementsByName("primaryContactField")[0].focus();
      });
    });
  }

  searchUsersItSponsor(searchText: any) {
    this.dataService
      .searchUsers(searchText, { excludeContractors: true })
      .subscribe((result) => {
        this.itSponsorOptions = _.orderBy(result["value"], [
          "surname",
          "givenName",
        ]);
        setTimeout(() => {
          document.getElementsByName("itSponsorField")[0].focus();
        });
      });
  }

  getITProjectClassification() {
    this.dataService.getAthenaData("wogrp_cc_value", {}).subscribe(
      (result) => {
        let startOfProjectClass = this.parseForITProjectClassification(
          JSON.stringify(result)
        );
        this.parseForProjectClass(
          JSON.stringify(result).substring(startOfProjectClass)
        );
      },
      (error) => {
        console.log("error", error);
        let errorType = "";
        let errorDescription = "";
        if (error.error.message) {
          errorDescription = errorDescription.concat(
            "Get IT Project Classification Error - ",
            error.error.message
          );
        } else {
          errorDescription = errorDescription.concat(
            "Get IT Project Classification Error"
          );
        }
        errorType = errorType.concat(error.status, " ", error.statusText);
        let dataStructure = {
          errorType: errorType,
          errorDescription: errorDescription,
        };
        this.dialog.open(ErrorDialogComponent, {
          data: dataStructure,
          disableClose: true,
        });
      }
    );
  }

  parseForITProjectClassification(arrayString: string): number {
    let i = 2;
    this.itProjectClassificationOptions = [];
    while (arrayString[i] !== "]") {
      let iStart = i;
      while (arrayString[i] !== "}") {
        i++;
      }
      let itProjectEntry = arrayString
        .substring(iStart, i + 1)
        .replace(/\\\"/g, '"')
        .replace(/\\\\/g, "\\");
      let entryParse = JSON.parse(itProjectEntry);
      this.itProjectClassificationOptions.push(entryParse.value);
      while (arrayString[i] !== "]" && arrayString[i] !== "{") {
        i++;
      }
    }
    this.itProjectClassificationOptions.sort();
    return i + 1;
  }

  parseForPatiProject(arrayString: string): number {
    let i = 2;
    this.patiProjectOptions = [];
    while (arrayString[i] !== "]") {
      let iStart = i;
      while (arrayString[i] !== "}") {
        i++;
      }
      let itProjectEntry = arrayString
        .substring(iStart, i + 1)
        .replace(/\\\"/g, '"')
        .replace(/\\\\/g, "\\");
      let entryParse = JSON.parse(itProjectEntry);
      this.patiProjectOptions.push(entryParse);
      while (arrayString[i] !== "]" && arrayString[i] !== "{") {
        i++;
      }
    }
    this.patiProjectOptions = this.patiProjectOptions.filter(function(p) {
      var id = p.Project_ID ? p.Project_ID.toString().trim() : "";
      if (/^(NINT|INT)/i.test(id)) { return false; }
      if (/^\d{5}$/.test(id)) { return false; }
      if (/^\d{6}$/.test(id) && parseInt(id, 10) <= 336593) { return false; }
      return true;
    });
    this.patiProjectOptions = _.sortBy(this.patiProjectOptions, "Project_ID");
    this.filteredPatiProjectOptions = this.patiProjectControl.valueChanges.pipe(
      startWith<string | PatiProject[]>(""),
      map((value) => (typeof value === "string" ? value : this.lastFilter)),
      map((filter) => this.patiProjectFilter(filter))
    );
    return i + 1;
  }

  parseForProjectClass(arrayString: string): number {
    let i = 1;
    this.itProjectClassOptions = [];
    while (arrayString[i] !== "]") {
      let iStart = i;
      while (arrayString[i] !== "}") {
        i++;
      }
      let itProjectEntry = arrayString
        .substring(iStart, i + 1)
        .replace(/\\\"/g, '"')
        .replace(/\\\\/g, "\\");
      let entryParse = JSON.parse(itProjectEntry);
      this.itProjectClassOptions.push(
        new ProjectClass(entryParse.work_order_grp_id, entryParse.description)
      );
      while (arrayString[i] !== "]" && arrayString[i] !== "{") {
        i++;
      }
    }
    this.itProjectClassOptions = _.sortBy(this.itProjectClassOptions, [
      "description",
    ]);
    return i + 1;
  }

  getPatiID() {
    this.gettingPatiIds = true;
    var callBody = {
      patiPrjId: "1",
    };
    this.dataService.getPatiValidation(callBody).subscribe(
      (result: any) => {
        this.parseForPatiProject(JSON.stringify(result.body));
        this.gettingPatiIds = false;
      },
      (error) => {
        console.log("error", error);
        let errorType = "";
        let errorDescription = "";
        if (error.error.message) {
          errorDescription = errorDescription.concat(
            "Get PaTi Error - ",
            error.error.message
          );
        } else {
          errorDescription = errorDescription.concat("Get PaTi Error");
        }
        errorType = errorType.concat(error.status, " ", error.statusText);
        let dataStructure = {
          errorType: errorType,
          errorDescription: errorDescription,
        };
        this.dialog.open(ErrorDialogComponent, {
          data: dataStructure,
          disableClose: true,
        });
        this.gettingPatiIds = false;
      }
    );
  }

  newPatiID() {
    this.patiValidated = false;
  }

  onStartDateChange() {
    if (this.datepickerInput1.nativeElement.value.match(/^\s*\d{4}\s*$/)) {
      this.estimatedStartDate = new Date(
        this.datepickerInput1.nativeElement.value,
        0
      );
    }
  }

  onInServiceDateChange() {
    if (this.datepickerInput2.nativeElement.value.match(/^\s*\d{4}\s*$/)) {
      this.estimatedInServiceDate = new Date(
        this.datepickerInput2.nativeElement.value,
        0
      );
    }
  }

  onInServiceYearSelection(date: Date, datePicker: any) {
    this.inServiceYear = new Date(date).getFullYear().toString();
    datePicker.close();
  }

  getInServiceDate() {
    if (this.inServiceYear) {
      return new Date(`${this.inServiceYear}/01/01`);
    }
    return new Date();
  }

  /*async getUsers() {
    let funcCall = await this.dataService.getUsers().toPromise();
    let nextLink = funcCall['@odata.nextLink'];
    let userList = funcCall['value'];
    let newUserList = [];
    while (nextLink) {
      funcCall = await this.dataService.getUsersContinued(nextLink).toPromise();
      nextLink = funcCall['@odata.nextLink'];
      newUserList = funcCall['value'];
      userList.concat(newUserList);
    }
    console.log(userList);
    /*this.dataService.getUsers().subscribe((result) => {
      let nextLink = result['@odata.nextLink']
      if (nextLink) {
        let callList = [this.dataService.getUsersContinued(nextLink)];
        for (const func in callList) {
          await func();
        }
      }
    });
  }*/

  clearSessionStorage() {
    let userData = JSON.parse(sessionStorage.getItem("userInfo"));
    let currentUser = JSON.parse(sessionStorage.getItem("currentUser"));
    let fullTokenPayload = JSON.parse(
      sessionStorage.getItem("fullTokenPayload")
    );
    sessionStorage.clear();
    sessionStorage.setItem("userInfo", JSON.stringify(userData));
    sessionStorage.setItem("currentUser", JSON.stringify(currentUser));
    sessionStorage.setItem(
      "fullTokenPayload",
      JSON.stringify(fullTokenPayload)
    );
  }

  startOver(): void {
    this.clearSessionStorage();
    window.location.reload();
  }

  exitPage() {
    this.router.navigate(["/dashboard"]);
  }

  isProjectConsistsOfCheckboxRequired() {
    if (
      this.hardwareOrSoftware === "O&M Only" ||
      this.hardwareOrSoftware === "Hardware Only"
    )
      return false;
    if (this.projectConsistsOf && this.projectConsistsOf.length > 0)
      return false;
    return true;
  }

  confirmAndContinue(form: NgForm) {
    //this.saveFundingProjectData();
    //this.router.navigate(['/charging-project-setup']);
    const isProjectConsistsOfCheckboxRequired =
      this.isProjectConsistsOfCheckboxRequired();

    // Validate primaryContact
    const isPrimaryContactValid = this.validateGraphUser(
      this.primaryContactControl,
      this.primaryContact,
      this.displayFnPrimaryContact
    );

    // Validate itSponsor
    const isItSponsorValid = this.validateGraphUser(
      this.itSponsorControl,
      this.itSponsor,
      this.displayFnITSponsor
    );

    if (!isPrimaryContactValid) {
      this.primaryContactControl.setErrors({ invalidContact: true });
    } else {
      this.primaryContactControl.setErrors(null);
    }

    if (!isItSponsorValid) {
      this.itSponsorControl.setErrors({ invalidSponsor: true });
    } else {
      this.itSponsorControl.setErrors(null);
    }

    if (
      form.invalid ||
      isProjectConsistsOfCheckboxRequired ||
      !isPrimaryContactValid ||
      !isItSponsorValid
    ) {
      this.showErrorsDisclaimer = true;
      this.scrollToErrorDisclaimer();
    }
    if (
      this.nextDisabled() ||
      form.invalid ||
      isProjectConsistsOfCheckboxRequired ||
      !isPrimaryContactValid ||
      !isItSponsorValid
    )
      return;

    this.saveProjectData();
    this.router.navigate(["/it-project-estimate"]);
  }

  scrollToErrorDisclaimer() {
    setTimeout(() => {
      const errorDisclaimerEl = document.getElementById("errorDisclaimerID");
      errorDisclaimerEl.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }

  validateGraphUser(
    control: FormControl,
    selectedContact: GraphUser,
    displayFn: (value: GraphUser) => string
  ): boolean {
    try {
      const inputValue = control.value;

      let contact = "";
      if (inputValue.givenName && inputValue.surname && inputValue.jobTitle) {
        contact =
          inputValue.givenName +
          " " +
          inputValue.surname +
          " (" +
          inputValue.mail +
          "), " +
          inputValue.jobTitle;
      } else if (
        inputValue.givenName &&
        inputValue.surname &&
        !inputValue.jobTitle
      ) {
        contact =
          inputValue.givenName +
          " " +
          inputValue.surname +
          " (" +
          inputValue.mail +
          ")";
      } else if (inputValue.displayName && inputValue.jobTitle) {
        contact =
          inputValue.displayName +
          " (" +
          inputValue.mail +
          "), " +
          inputValue.jobTitle;
      } else if (inputValue.displayName && !inputValue.jobTitle) {
        contact = inputValue.displayName + " (" + inputValue.mail + ")";
      }
      const isValid = contact === displayFn(selectedContact);
      return isValid;
    } catch (e) {
      return false;
    }
  }

  saveProjectData() {
    if (this.patiProjectOptions) {
      sessionStorage.setItem(
        "patiProjectOptions",
        JSON.stringify(this.patiProjectOptions)
      );
    }
    if (this.patiProject) {
      sessionStorage.setItem("patiProject", JSON.stringify(this.patiProject));
    }
    if (this.projectName) {
      sessionStorage.setItem("projectName", this.projectName);
    }
    if (this.itProjectClassificationOptions) {
      sessionStorage.setItem(
        "itProjectClassificationOptions",
        JSON.stringify(this.itProjectClassificationOptions)
      );
    }
    if (this.itProjectClassification) {
      sessionStorage.setItem(
        "itProjectClassification",
        JSON.stringify(this.itProjectClassification)
      );
    }
    if (this.itProjectClassOptions) {
      sessionStorage.setItem(
        "itProjectClassOptions",
        JSON.stringify(this.itProjectClassOptions)
      );
    }
    if (this.itProjectClass) {
      sessionStorage.setItem(
        "itProjectClass",
        JSON.stringify(this.itProjectClass)
      );
    }
    if (this.isLighthouseProjectOptions) {
      sessionStorage.setItem(
        "isLighthouseProjectOptions",
        JSON.stringify(this.isLighthouseProjectOptions)
      );
    }
    if (this.isLighthouseProject) {
      sessionStorage.setItem("isLighthouseProject", this.isLighthouseProject);
    }
    if (this.estimatedStartDate) {
      sessionStorage.setItem(
        "estimatedStartDate",
        this.convertToDateStringFormat(this.estimatedStartDate)
      );
    }
    if (this.estimatedInServiceDate) {
      sessionStorage.setItem(
        "estimatedInServiceDate",
        this.convertToDateStringFormat(this.estimatedInServiceDate)
      );
    }
    if (this.projectScopeDescription) {
      sessionStorage.setItem(
        "projectScopeDescription",
        this.projectScopeDescription
      );
    }
    if (this.primaryContactOptions) {
      sessionStorage.setItem(
        "primaryContactOptions",
        JSON.stringify(this.primaryContactOptions)
      );
    }
    if (this.primaryContact) {
      sessionStorage.setItem(
        "primaryContact",
        JSON.stringify(this.primaryContact)
      );
    }
    if (this.itSponsorOptions) {
      sessionStorage.setItem(
        "itSponsorOptions",
        JSON.stringify(this.itSponsorOptions)
      );
    }
    if (this.itSponsor) {
      sessionStorage.setItem("itSponsor", JSON.stringify(this.itSponsor));
    }
    if (this.hardwareOrSoftwareOptions) {
      sessionStorage.setItem(
        "hardwareOrSoftwareOptions",
        JSON.stringify(this.hardwareOrSoftwareOptions)
      );
    }
    if (this.omOnly) {
      sessionStorage.setItem("omOnly", this.omOnly);
    }
    if (this.relatedWork) {
      sessionStorage.setItem("relatedWork", this.relatedWork);
    }
    if (this.hardwareOrSoftware) {
      sessionStorage.setItem("hardwareOrSoftware", this.hardwareOrSoftware);
    }
    if (this.projectConsistsOfOptions) {
      sessionStorage.setItem(
        "projectConsistsOfOptions",
        JSON.stringify(this.projectConsistsOfOptions)
      );
    }
    if (this.projectConsistsOfCheckboxes) {
      sessionStorage.setItem(
        "projectConsistsOfCheckboxes",
        JSON.stringify(this.projectConsistsOfCheckboxes)
      );
    }
    if (this.projectConsistsOf) {
      sessionStorage.setItem(
        "projectConsistsOf",
        JSON.stringify(this.projectConsistsOf)
      );
    }
    if (this.inServiceYear) {
      sessionStorage.setItem("inServiceYear", this.inServiceYear);
    }
    if (this.softwareBeingUsed) {
      sessionStorage.setItem("softwareBeingUsed", this.softwareBeingUsed);
    }
    if (this.softwareProjectOverview) {
      sessionStorage.setItem(
        "softwareProjectOverview",
        this.softwareProjectOverview
      );
    }
    if (this.vendorName) {
      sessionStorage.setItem("vendorName", this.vendorName);
    }
    if (this.softwareName) {
      sessionStorage.setItem("softwareName", this.softwareName);
    }
    if (this.dataConversionActivities) {
      sessionStorage.setItem(
        "dataConversionActivities",
        this.dataConversionActivities
      );
    }
    if (this.trainingCosts) {
      sessionStorage.setItem("trainingCosts", this.trainingCosts);
    }
    if (this.associatedRetirement) {
      sessionStorage.setItem("associatedRetirement", this.associatedRetirement);
    }
    if (this.softwareToBeRetired) {
      sessionStorage.setItem("softwareToBeRetired", this.softwareToBeRetired);
    }
    if (this.retiredInServiceDate) {
      sessionStorage.setItem("retiredInServiceDate", this.retiredInServiceDate);
    }
    if (this.replacingInterfaces) {
      sessionStorage.setItem("replacingInterfaces", this.replacingInterfaces);
    }
    if (this.howSoftwareReleasedOptions) {
      sessionStorage.setItem(
        "howSoftwareReleasedOptions",
        JSON.stringify(this.howSoftwareReleasedOptions)
      );
    }
    if (this.howSoftwareReleased) {
      sessionStorage.setItem("howSoftwareReleased", this.howSoftwareReleased);
    }
    if (this.phasedRollOuts) {
      sessionStorage.setItem(
        "phasedRollOuts",
        JSON.stringify(this.phasedRollOuts)
      );
    }
    if (this.capabilities) {
      sessionStorage.setItem("capabilities", JSON.stringify(this.capabilities));
    }
    if (this.functionalities) {
      sessionStorage.setItem(
        "functionalities",
        JSON.stringify(this.functionalities)
      );
    }
    if (this.purchasingWarranties) {
      sessionStorage.setItem("purchasingWarranties", this.purchasingWarranties);
    }
    if (this.expectedLife) {
      sessionStorage.setItem("expectedLife", this.expectedLife.toString());
    }
    if (this.agileEffort) {
      sessionStorage.setItem("agileEffort", this.agileEffort);
    }
    if (this.maintenanceContracts) {
      sessionStorage.setItem("maintenanceContracts", this.maintenanceContracts);
    }
    if (this.perpetualLicenses) {
      sessionStorage.setItem("perpetualLicenses", this.perpetualLicenses);
    }
    if (this.contractualRight) {
      sessionStorage.setItem("contractualRight", this.contractualRight);
    }
    if (this.significantPenalty) {
      sessionStorage.setItem("significantPenalty", this.significantPenalty);
    }
    if (this.isFeasible) {
      sessionStorage.setItem("isFeasible", this.isFeasible);
    }
    if (this.serviceFees) {
      sessionStorage.setItem("serviceFees", this.serviceFees);
    }
    if (this.useWithoutPaying) {
      sessionStorage.setItem("useWithoutPaying", this.useWithoutPaying);
    }
    if (this.draftSaved) {
      sessionStorage.setItem("draftSaved", this.draftSaved);
    }
    if (this.draftSavedDay) {
      sessionStorage.setItem("draftSavedDay", this.draftSavedDay);
    }
    if (this.draftSavedTime) {
      sessionStorage.setItem("draftSavedTime", this.draftSavedTime);
    }
    if (this.approverList) {
      sessionStorage.setItem("approverList", JSON.stringify(this.approverList));
    }
    if (this.department) {
      sessionStorage.setItem("department", JSON.stringify(this.department));
    }
    if (this.buildingInfo) {
      sessionStorage.setItem("buildingInfo", JSON.stringify(this.buildingInfo));
    }
    if (this.distributeCost) {
      sessionStorage.setItem(
        "distributeCost",
        JSON.stringify(this.distributeCost)
      );
    }
    if (this.defaultOption) {
      sessionStorage.setItem(
        "defaultOption",
        JSON.stringify(this.defaultOption)
      );
    }
    if (this.releaseDetails) {
      sessionStorage.setItem(
        "releaseDetails",
        JSON.stringify(this.releaseDetails)
      );
    }
    if (this.todayDate) {
      sessionStorage.setItem("todayDate", JSON.stringify(this.todayDate));
    }
    if (this.allocations) {
      for (let i = 0; i < this.allocations.length; i++) {
        if (
          !this.allocations[i].operatingUnit ||
          !this.allocations[i].operatingUnit.OPERATING_UNIT
        ) {
          this.allocations[i].operatingUnit = null;
        }
      }
      sessionStorage.setItem("allocations", JSON.stringify(this.allocations));
    }
    if (this.buildings) {
      sessionStorage.setItem("buildings", JSON.stringify(this.buildings));
    }
    if (this.laborDetails) {
      for (let i = 0; i < this.laborDetails.length; i++) {
        if (
          !this.laborDetails[i].workTask ||
          !this.laborDetails[i].workTask.workTask
        ) {
          this.laborDetails[i].workTask = {
            workTask: "",
          };
        }
      }
      sessionStorage.setItem("laborDetails", JSON.stringify(this.laborDetails));
    }
    if (this.contingencyDetails) {
      sessionStorage.setItem(
        "contingencyDetails",
        JSON.stringify(this.contingencyDetails)
      );
    }
    if (this.additionalComments) {
      sessionStorage.setItem(
        "additionalComments",
        JSON.stringify(this.additionalComments)
      );
    }
    if (this.additionalApprovers) {
      sessionStorage.setItem(
        "additionalApprovers",
        JSON.stringify(this.additionalApprovers)
      );
    }
    if (this.approverList) {
      sessionStorage.setItem("approverList", JSON.stringify(this.approverList));
    }
    if (this.doaAmounts) {
      sessionStorage.setItem("doaAmounts", JSON.stringify(this.doaAmounts));
    }
    if (this.files) {
      sessionStorage.setItem("files", JSON.stringify(this.files));
    }
    if (this.itSparGuid) {
      sessionStorage.setItem("itSparGuid", JSON.stringify(this.itSparGuid));
    }
    sessionStorage.setItem(
      "isRevisionIndicator",
      JSON.stringify(this.isRevisionIndicator)
    );
    if (this.formNumber) {
      sessionStorage.setItem("formNumber", this.formNumber);
    }
    sessionStorage.setItem(
      "displayRevisionNumber",
      this.displayRevisionNumber.toString()
    );
    sessionStorage.setItem(
      "previousCapitalSubtotal",
      this.previousCapitalSubtotal.toString()
    );
    sessionStorage.setItem(
      "previousOMSubtotal",
      this.previousOMSubtotal.toString()
    );
    if (this.etsPacAssignment) {
      sessionStorage.setItem(
        "etsPacAssignment",
        JSON.stringify(this.etsPacAssignment)
      );
    }
  }

  loadProjectData() {
    let patiProjectOptionsCheck = sessionStorage.getItem("patiProjectOptions");
    if (patiProjectOptionsCheck) {
      this.patiProjectOptions = JSON.parse(patiProjectOptionsCheck);
      this.filteredPatiProjectOptions =
        this.patiProjectControl.valueChanges.pipe(
          startWith<string | PatiProject[]>(""),
          map((value) => (typeof value === "string" ? value : this.lastFilter)),
          map((filter) => this.patiProjectFilter(filter))
        );
    } else {
      this.patiProjectOptions = [];
    }
    let patiProjectCheck = sessionStorage.getItem("patiProject");
    if (patiProjectCheck) {
      this.patiProject = JSON.parse(patiProjectCheck);
      this.displayPatiProject = this.patiProject.Project_ID.concat(
        " - "
      ).concat(this.patiProject.Project_Name);
    } else {
      this.patiProject = null;
      this.displayPatiProject = "";
    }
    let projectNameCheck = sessionStorage.getItem("projectName");
    if (projectNameCheck) {
      this.projectName = projectNameCheck;
    } else {
      this.projectName = "";
    }
    let itProjectClassificationOptionsCheck = sessionStorage.getItem(
      "itProjectClassificationOptions"
    );
    if (itProjectClassificationOptionsCheck) {
      this.itProjectClassificationOptions = JSON.parse(
        itProjectClassificationOptionsCheck
      );
      this.filteredItProjectClassificationOptions =
        this.itProjectClassificationControl.valueChanges.pipe(
          startWith<string | string[]>(""),
          map((value) => (typeof value === "string" ? value : this.lastFilter)),
          map((filter) => this.itProjectClassificationFilter(filter))
        );
    } else {
      this.itProjectClassificationOptions = [];
    }
    let itProjectClassificationCheck = sessionStorage.getItem(
      "itProjectClassification"
    );
    if (itProjectClassificationCheck) {
      this.itProjectClassification = JSON.parse(itProjectClassificationCheck);
    } else {
      this.itProjectClassification = null;
    }
    let itProjectClassOptionsCheck = sessionStorage.getItem(
      "itProjectClassOptions"
    );
    if (itProjectClassOptionsCheck) {
      this.itProjectClassOptions = JSON.parse(itProjectClassOptionsCheck);
    } else {
      this.itProjectClassOptions = [];
    }
    let itProjectClassCheck = sessionStorage.getItem("itProjectClass");
    if (itProjectClassCheck) {
      this.itProjectClass = JSON.parse(itProjectClassCheck);
    } else {
      this.itProjectClass = null;
    }
    let isLighthouseProjectOptionsCheck = sessionStorage.getItem(
      "isLighthouseProjectOptions"
    );
    if (isLighthouseProjectOptionsCheck) {
      this.isLighthouseProjectOptions = JSON.parse(
        isLighthouseProjectOptionsCheck
      );
    } else {
      this.isLighthouseProjectOptions = [];
    }
    let isLighthouseProjectCheck = sessionStorage.getItem(
      "isLighthouseProject"
    );
    if (isLighthouseProjectCheck) {
      this.isLighthouseProject = isLighthouseProjectCheck;
    } else {
      this.isLighthouseProject = "NO";
    }
    let startDateCheck = sessionStorage.getItem("estimatedStartDate");
    if (startDateCheck) {
      this.estimatedStartDate = new Date(startDateCheck);
      this.estimatedStartDate.setDate(this.estimatedStartDate.getDate() + 1);
    } else {
      this.estimatedStartDate = null;
    }
    let inServiceDateCheck = sessionStorage.getItem("estimatedInServiceDate");
    if (inServiceDateCheck) {
      this.estimatedInServiceDate = new Date(inServiceDateCheck);
      this.estimatedInServiceDate.setDate(
        this.estimatedInServiceDate.getDate() + 1
      );
    } else {
      this.estimatedInServiceDate = null;
    }
    let projectScopeDescriptionCheck = sessionStorage.getItem(
      "projectScopeDescription"
    );
    if (projectScopeDescriptionCheck) {
      this.projectScopeDescription = projectScopeDescriptionCheck;
    } else {
      this.projectScopeDescription = "";
    }
    let primaryContactOptionsCheck = sessionStorage.getItem(
      "primaryContactOptions"
    );
    if (primaryContactOptionsCheck) {
      this.primaryContactOptions = JSON.parse(primaryContactOptionsCheck);
    } else {
      this.primaryContactOptions = [];
    }
    let primaryContactCheck = sessionStorage.getItem("primaryContact");
    if (primaryContactCheck) {
      this.primaryContact = JSON.parse(primaryContactCheck);
    } else {
      this.primaryContact = null;
    }
    let itSponsorOptionsCheck = sessionStorage.getItem("itSponsorOptions");
    if (itSponsorOptionsCheck) {
      this.itSponsorOptions = JSON.parse(itSponsorOptionsCheck);
    } else {
      this.itSponsorOptions = [];
    }
    let itSponsorCheck = sessionStorage.getItem("itSponsor");
    if (itSponsorCheck) {
      this.itSponsor = JSON.parse(itSponsorCheck);
    } else {
      this.itSponsor = null;
    }
    let hardwareOrSoftwareOptionsCheck = sessionStorage.getItem(
      "hardwareOrSoftwareOptions"
    );
    if (hardwareOrSoftwareOptionsCheck) {
      this.hardwareOrSoftwareOptions = JSON.parse(
        hardwareOrSoftwareOptionsCheck
      );
    } else {
      this.hardwareOrSoftwareOptions = [];
    }
    let omOnlycheck = sessionStorage.getItem("omOnly");
    this.omOnly = omOnlycheck ? omOnlycheck : null;

    let relatedWorkCheck = sessionStorage.getItem("relatedWork");
    this.relatedWork = relatedWorkCheck ? relatedWorkCheck : null;

    let hardwareOrSoftwareCheck = sessionStorage.getItem("hardwareOrSoftware");
    if (hardwareOrSoftwareCheck) {
      this.hardwareOrSoftware = hardwareOrSoftwareCheck;
      this.hardwareOrSoftwarePrevious = hardwareOrSoftwareCheck;
    } else {
      this.hardwareOrSoftware = null;
      this.hardwareOrSoftwarePrevious = null;
    }
    let projectConsistsOfCheck = sessionStorage.getItem("projectConsistsOf");
    if (projectConsistsOfCheck) {
      this.projectConsistsOf = JSON.parse(projectConsistsOfCheck);
    } else {
      this.projectConsistsOf = [];
    }
    let projectConsistsOfCheckboxesCheck = sessionStorage.getItem(
      "projectConsistsOfCheckboxes"
    );
    if (projectConsistsOfCheckboxesCheck) {
      this.projectConsistsOfCheckboxes = JSON.parse(
        projectConsistsOfCheckboxesCheck
      );
    } else {
      this.projectConsistsOfCheckboxes = [];
    }
    let projectConsistsOfOptionsCheck = sessionStorage.getItem(
      "projectConsistsOfOptions"
    );
    if (projectConsistsOfOptionsCheck) {
      this.projectConsistsOfOptions = JSON.parse(projectConsistsOfOptionsCheck);
    } else {
      this.projectConsistsOfOptions = [];
    }
    let inServiceYearCheck = sessionStorage.getItem("inServiceYear");
    if (inServiceYearCheck) {
      this.inServiceYear = inServiceYearCheck;
    } else {
      this.inServiceYear = "";
    }
    let softwareBeingUsedCheck = sessionStorage.getItem("softwareBeingUsed");
    if (softwareBeingUsedCheck) {
      this.softwareBeingUsed = softwareBeingUsedCheck;
    } else {
      this.softwareBeingUsed = "";
    }
    let softwareProjectOverviewCheck = sessionStorage.getItem(
      "softwareProjectOverview"
    );
    if (softwareProjectOverviewCheck) {
      this.softwareProjectOverview = softwareProjectOverviewCheck;
    } else {
      this.softwareProjectOverview = "";
    }
    let vendorNameCheck = sessionStorage.getItem("vendorName");
    if (vendorNameCheck) {
      this.vendorName = vendorNameCheck;
    } else {
      this.vendorName = "";
    }
    let softwareNameCheck = sessionStorage.getItem("softwareName");
    if (softwareNameCheck) {
      this.softwareName = softwareNameCheck;
    } else {
      this.softwareName = "";
    }
    let dataConversionActivitiesCheck = sessionStorage.getItem(
      "dataConversionActivities"
    );
    if (dataConversionActivitiesCheck) {
      this.dataConversionActivities = dataConversionActivitiesCheck;
    } else {
      this.dataConversionActivities = "";
    }
    let trainingCostsCheck = sessionStorage.getItem("trainingCosts");
    if (trainingCostsCheck) {
      this.trainingCosts = trainingCostsCheck;
    } else {
      this.trainingCosts = "";
    }
    let associatedRetirementCheck = sessionStorage.getItem(
      "associatedRetirement"
    );
    if (associatedRetirementCheck) {
      this.associatedRetirement = associatedRetirementCheck;
    } else {
      this.associatedRetirement = "";
    }
    let softwareToBeRetiredCheck = sessionStorage.getItem(
      "softwareToBeRetired"
    );
    if (softwareToBeRetiredCheck) {
      this.softwareToBeRetired = softwareToBeRetiredCheck;
    } else {
      this.softwareToBeRetired = "";
    }
    let retiredInServiceDateCheck = sessionStorage.getItem(
      "retiredInServiceDate"
    );
    if (retiredInServiceDateCheck) {
      this.retiredInServiceDate = retiredInServiceDateCheck;
    } else {
      this.retiredInServiceDate = "";
    }
    let replacingInterfacesCheck = sessionStorage.getItem(
      "replacingInterfaces"
    );
    if (replacingInterfacesCheck) {
      this.replacingInterfaces = replacingInterfacesCheck;
    } else {
      this.replacingInterfaces = "";
    }
    let howSoftwareReleasedOptionsCheck = sessionStorage.getItem(
      "howSoftwareReleasedOptions"
    );
    if (howSoftwareReleasedOptionsCheck) {
      this.howSoftwareReleasedOptions = JSON.parse(
        howSoftwareReleasedOptionsCheck
      );
      this.filteredHowSoftwareReleasedOptions =
        this.howSoftwareReleasedControl.valueChanges.pipe(
          startWith<string | string[]>(""),
          map((value) => (typeof value === "string" ? value : this.lastFilter)),
          map((filter) => this.howSoftwareReleasedFilter(filter))
        );
    } else {
      this.howSoftwareReleasedOptions = [];
    }
    let howSoftwareReleasedCheck = sessionStorage.getItem(
      "howSoftwareReleased"
    );
    if (howSoftwareReleasedCheck) {
      this.howSoftwareReleased = howSoftwareReleasedCheck;
    } else {
      this.howSoftwareReleased = "";
    }
    let phasedRollOutsCheck = sessionStorage.getItem("phasedRollOuts");
    if (phasedRollOutsCheck) {
      this.phasedRollOuts = JSON.parse(phasedRollOutsCheck);
    } else {
      this.phasedRollOuts = [new PhasedRollOut()];
    }
    let capabilitiesCheck = sessionStorage.getItem("capabilities");
    if (capabilitiesCheck) {
      this.capabilities = JSON.parse(capabilitiesCheck);
    } else {
      this.capabilities = [new Capability()];
    }
    let functionalitiesCheck = sessionStorage.getItem("functionalities");
    if (functionalitiesCheck) {
      this.functionalities = JSON.parse(functionalitiesCheck);
    } else {
      this.functionalities = [new Functionality()];
    }
    let purchasingWarrantiesCheck = sessionStorage.getItem(
      "purchasingWarranties"
    );
    if (purchasingWarrantiesCheck) {
      this.purchasingWarranties = purchasingWarrantiesCheck;
    } else {
      this.purchasingWarranties = "";
    }
    let expectedLifeCheck = sessionStorage.getItem("expectedLife");
    if (expectedLifeCheck) {
      this.expectedLife = Number(expectedLifeCheck);
    } else {
      this.expectedLife = null;
    }
    let agileEffortCheck = sessionStorage.getItem("agileEffort");
    if (agileEffortCheck) {
      this.agileEffort = agileEffortCheck;
    } else {
      this.agileEffort = "";
    }
    let maintenanceContractsCheck = sessionStorage.getItem(
      "maintenanceContracts"
    );
    if (maintenanceContractsCheck) {
      this.maintenanceContracts = maintenanceContractsCheck;
    } else {
      this.maintenanceContracts = "";
    }
    let perpetualLicensesCheck = sessionStorage.getItem("perpetualLicenses");
    if (perpetualLicensesCheck) {
      this.perpetualLicenses = perpetualLicensesCheck;
    } else {
      this.perpetualLicenses = "";
    }
    let contractualRightCheck = sessionStorage.getItem("contractualRight");
    if (contractualRightCheck) {
      this.contractualRight = contractualRightCheck;
    } else {
      this.contractualRight = "";
    }
    let significantPenaltyCheck = sessionStorage.getItem("significantPenalty");
    if (significantPenaltyCheck) {
      this.significantPenalty = significantPenaltyCheck;
    } else {
      this.significantPenalty = "";
    }
    let isFeasibleCheck = sessionStorage.getItem("isFeasible");
    if (isFeasibleCheck) {
      this.isFeasible = isFeasibleCheck;
    } else {
      this.isFeasible = "";
    }
    let serviceFeesCheck = sessionStorage.getItem("serviceFees");
    if (serviceFeesCheck) {
      this.serviceFees = serviceFeesCheck;
    } else {
      this.serviceFees = "";
    }
    let useWithoutPayingCheck = sessionStorage.getItem("useWithoutPaying");
    if (useWithoutPayingCheck) {
      this.useWithoutPaying = useWithoutPayingCheck;
    } else {
      this.useWithoutPaying = "";
    }
    let departmentCheck = sessionStorage.getItem("department");
    if (departmentCheck) {
      this.department = JSON.parse(departmentCheck);
    } else {
      this.department = null;
    }
    let buildingInfoCheck = sessionStorage.getItem("buildingInfo");
    if (buildingInfoCheck) {
      this.buildingInfo = JSON.parse(buildingInfoCheck);
    } else {
      this.buildingInfo = null;
    }
    let releaseDetailsCheck = sessionStorage.getItem("releaseDetails");
    if (releaseDetailsCheck) {
      this.releaseDetails = JSON.parse(releaseDetailsCheck);
    } else {
      this.releaseDetails = [new ReleaseDetail()];
    }
    let distributeCostCheck = sessionStorage.getItem("distributeCost");
    if (distributeCostCheck) {
      this.distributeCost = JSON.parse(distributeCostCheck);
    } else {
      this.distributeCost = "Manually";
    }
    let defaultOptionCheck = sessionStorage.getItem("defaultOption");
    if (defaultOptionCheck) {
      this.defaultOption = JSON.parse(defaultOptionCheck);
    } else {
      this.defaultOption = null;
    }
    let allocationsCheck = sessionStorage.getItem("allocations");
    if (allocationsCheck) {
      this.allocations = JSON.parse(allocationsCheck);
    } else {
      this.allocations = [new Allocation()];
    }
    let buildingsCheck = sessionStorage.getItem("buildings");
    if (buildingsCheck) {
      this.buildings = JSON.parse(buildingsCheck);
    } else {
      this.buildings = [new Building()];
    }
    let laborDetailsCheck = sessionStorage.getItem("laborDetails");
    if (laborDetailsCheck) {
      this.laborDetails = JSON.parse(laborDetailsCheck);
    } else {
      this.laborDetails = [new LaborDetail()];
    }
    let contingencyDetailsCheck = sessionStorage.getItem("contingencyDetails");
    if (contingencyDetailsCheck) {
      this.contingencyDetails = JSON.parse(contingencyDetailsCheck);
    } else {
      this.contingencyDetails = [new ContingencyDetail()];
    }
    let additionalCommentsCheck = sessionStorage.getItem("additionalComments");
    if (additionalCommentsCheck) {
      this.additionalComments = JSON.parse(additionalCommentsCheck);
    } else {
      this.additionalComments = null;
    }
    let filesCheck = sessionStorage.getItem("files");
    if (filesCheck) {
      this.files = JSON.parse(filesCheck);
    } else {
      this.files = [];
    }
    let additionalApproversCheck = sessionStorage.getItem(
      "additionalApprovers"
    );
    if (additionalApproversCheck) {
      this.additionalApprovers = JSON.parse(additionalApproversCheck);
    } else {
      this.additionalApprovers = [];
    }
    let doaAmountsCheck = sessionStorage.getItem("doaAmounts");
    if (doaAmountsCheck) {
      this.doaAmounts = JSON.parse(doaAmountsCheck);
    } else {
      this.doaAmounts = [0];
    }
    let itSparGuidCheck = sessionStorage.getItem("itSparGuid");
    if (itSparGuidCheck) {
      this.itSparGuid = JSON.parse(itSparGuidCheck);
    } else {
      this.itSparGuid = "";
    }
    let draftSavedCheck = sessionStorage.getItem("draftSaved");
    if (draftSavedCheck) {
      this.draftSaved = draftSavedCheck;
    } else {
      this.draftSaved = "";
    }
    let draftSavedDayCheck = sessionStorage.getItem("draftSavedDay");
    if (draftSavedDayCheck) {
      this.draftSavedDay = draftSavedDayCheck;
    } else {
      this.draftSavedDay = "";
    }
    let draftSavedTimeCheck = sessionStorage.getItem("draftSavedTime");
    if (draftSavedTimeCheck) {
      this.draftSavedTime = draftSavedTimeCheck;
    } else {
      this.draftSavedTime = "";
    }
    let approverListCheck = sessionStorage.getItem("approverList");
    if (approverListCheck) {
      this.approverList = JSON.parse(approverListCheck);
    } else {
      this.approverList = [];
    }
    let isRevisionIndicatorCheck = sessionStorage.getItem(
      "isRevisionIndicator"
    );
    if (isRevisionIndicatorCheck && isRevisionIndicatorCheck === "true") {
      this.isRevisionIndicator = true;
    } else {
      this.isRevisionIndicator = false;
    }
    let formNumberCheck = sessionStorage.getItem("formNumber");
    if (formNumberCheck) {
      this.formNumber = formNumberCheck;
    } else {
      this.formNumber = "";
    }
    let displayRevisionNumberCheck = sessionStorage.getItem(
      "displayRevisionNumber"
    );
    if (displayRevisionNumberCheck) {
      this.displayRevisionNumber = Number(displayRevisionNumberCheck);
    } else {
      this.displayRevisionNumber = 0;
    }
    let previousCapitalSubtotalCheck = sessionStorage.getItem(
      "previousCapitalSubtotal"
    );
    if (previousCapitalSubtotalCheck) {
      this.previousCapitalSubtotal = Number(previousCapitalSubtotalCheck);
    } else {
      this.previousCapitalSubtotal = 0;
    }
    let previousOMSubtotalCheck = sessionStorage.getItem("previousOMSubtotal");
    if (previousOMSubtotalCheck) {
      this.previousOMSubtotal = Number(previousOMSubtotalCheck);
    } else {
      this.previousOMSubtotal = 0;
    }
    this.sparDataLoaded = true;
    let etsPacAssignmentCheck = sessionStorage.getItem("etsPacAssignment");
    if (etsPacAssignmentCheck) {
      this.etsPacAssignment = JSON.parse(etsPacAssignmentCheck);
    } else {
      this.etsPacAssignment = null;
    }
  }
  base64ImageToBlob(str) {
    // extract content type and base64 payload from original string
    let pos = str.indexOf(";base64,");
    let type = str.substring(5, pos);
    let b64 = str.substr(pos + 8);

    // decode base64
    let imageContent = atob(b64);

    // create an ArrayBuffer and a view (as unsigned 8-bit)
    let buffer = new ArrayBuffer(imageContent.length);
    let view = new Uint8Array(buffer);

    // fill the view, using the decoded base64
    for (let n = 0; n < imageContent.length; n++) {
      view[n] = imageContent.charCodeAt(n);
    }

    // convert ArrayBuffer to Blob
    let blob = new Blob([buffer], { type: type });

    return blob;
  }

  convertToDateStringFormat(date: Date): string {
    if (date) {
      return (
        date.getFullYear().toString() +
        "-" +
        (date.getMonth() + 1).toString().padStart(2, "0") +
        "-" +
        date.getDate().toString().padStart(2, "0")
      );
    } else {
      return null;
    }
  }

  nextDisabled() {
    if (
      !(
        this.patiProject &&
        this.patiProject.Project_ID &&
        this.projectName &&
        this.itProjectClassification &&
        this.estimatedStartDate &&
        this.estimatedInServiceDate &&
        this.projectScopeDescription &&
        this.primaryContact &&
        this.primaryContact.mail &&
        this.itSponsor &&
        this.itSponsor.mail &&
        this.hardwareOrSoftware
      )
    ) {
      return true;
    } else if (
      this.patiProject &&
      this.patiProject.Project_ID &&
      this.projectName &&
      this.itProjectClassification &&
      this.estimatedStartDate &&
      this.estimatedInServiceDate &&
      this.projectScopeDescription &&
      this.primaryContact &&
      this.primaryContact.mail &&
      this.itSponsor &&
      this.itSponsor.mail &&
      this.hardwareOrSoftware
    ) {
      if (this.hardwareOrSoftware === "O&M Only") {
        return false;
      } else if (this.hardwareOrSoftware === "Hardware Only") {
        return !this.purchasingWarranties;
      } else if (this.hardwareOrSoftware === "Software Only") {
        if (
          !(
            this.projectConsistsOf &&
            this.projectConsistsOf.length > 0 &&
            this.softwareBeingUsed &&
            this.softwareProjectOverview &&
            this.dataConversionActivities &&
            this.trainingCosts &&
            this.associatedRetirement &&
            this.replacingInterfaces &&
            this.howSoftwareReleased
          )
        ) {
          return true;
        } else if (
          this.projectConsistsOf &&
          this.projectConsistsOf.length > 0 &&
          this.softwareBeingUsed &&
          this.softwareProjectOverview &&
          this.dataConversionActivities &&
          this.trainingCosts &&
          this.associatedRetirement &&
          this.replacingInterfaces &&
          this.howSoftwareReleased
        ) {
          if (this.softwareBeingUsed === "NO") {
            if (this.associatedRetirement === "YES") {
              if (this.softwareToBeRetired) {
                if (this.isVendorHosted()) {
                  return !(
                    this.perpetualLicenses &&
                    this.contractualRight &&
                    this.significantPenalty &&
                    this.isFeasible &&
                    this.serviceFees &&
                    this.useWithoutPaying
                  );
                } else {
                  return false;
                }
              } else {
                return true;
              }
            } else if (this.associatedRetirement === "NO") {
              if (this.isVendorHosted()) {
                return !(
                  this.perpetualLicenses &&
                  this.contractualRight &&
                  this.significantPenalty &&
                  this.isFeasible &&
                  this.serviceFees &&
                  this.useWithoutPaying
                );
              } else {
                return false;
              }
            }
          }
          if (this.softwareBeingUsed === "YES") {
            if (this.inServiceYear) {
              if (this.associatedRetirement === "YES") {
                if (this.softwareToBeRetired) {
                  if (this.isVendorHosted()) {
                    return !(
                      this.perpetualLicenses &&
                      this.contractualRight &&
                      this.significantPenalty &&
                      this.isFeasible &&
                      this.serviceFees &&
                      this.useWithoutPaying
                    );
                  } else {
                    return false;
                  }
                } else {
                  return true;
                }
              } else if (this.associatedRetirement === "NO") {
                if (this.isVendorHosted()) {
                  return !(
                    this.perpetualLicenses &&
                    this.contractualRight &&
                    this.significantPenalty &&
                    this.isFeasible &&
                    this.serviceFees &&
                    this.useWithoutPaying
                  );
                } else {
                  return false;
                }
              }
            } else {
              return true;
            }
          }
        }
      } else if (this.hardwareOrSoftware === "Hardware and Software") {
        if (
          !(
            this.purchasingWarranties &&
            this.projectConsistsOf &&
            this.projectConsistsOf.length > 0 &&
            this.softwareBeingUsed &&
            this.softwareProjectOverview &&
            this.dataConversionActivities &&
            this.trainingCosts &&
            this.associatedRetirement &&
            this.replacingInterfaces &&
            this.howSoftwareReleased
          )
        ) {
          return true;
        } else if (
          this.purchasingWarranties &&
          this.projectConsistsOf &&
          this.projectConsistsOf.length > 0 &&
          this.softwareBeingUsed &&
          this.softwareProjectOverview &&
          this.dataConversionActivities &&
          this.trainingCosts &&
          this.associatedRetirement &&
          this.replacingInterfaces &&
          this.howSoftwareReleased
        ) {
          if (this.softwareBeingUsed === "NO") {
            if (this.associatedRetirement === "YES") {
              if (this.softwareToBeRetired) {
                if (this.isVendorHosted()) {
                  return !(
                    this.perpetualLicenses &&
                    this.contractualRight &&
                    this.significantPenalty &&
                    this.isFeasible &&
                    this.serviceFees &&
                    this.useWithoutPaying
                  );
                } else {
                  return false;
                }
              } else {
                return true;
              }
            } else if (this.associatedRetirement === "NO") {
              if (this.isVendorHosted()) {
                return !(
                  this.perpetualLicenses &&
                  this.contractualRight &&
                  this.significantPenalty &&
                  this.isFeasible &&
                  this.serviceFees &&
                  this.useWithoutPaying
                );
              } else {
                return false;
              }
            }
          }
          if (this.softwareBeingUsed === "YES") {
            if (this.inServiceYear) {
              if (this.associatedRetirement === "YES") {
                if (this.softwareToBeRetired) {
                  if (this.isVendorHosted()) {
                    return !(
                      this.perpetualLicenses &&
                      this.contractualRight &&
                      this.significantPenalty &&
                      this.isFeasible &&
                      this.serviceFees &&
                      this.useWithoutPaying
                    );
                  } else {
                    return false;
                  }
                } else {
                  return true;
                }
              } else if (this.associatedRetirement === "NO") {
                if (this.isVendorHosted()) {
                  return !(
                    this.perpetualLicenses &&
                    this.contractualRight &&
                    this.significantPenalty &&
                    this.isFeasible &&
                    this.serviceFees &&
                    this.useWithoutPaying
                  );
                } else {
                  return false;
                }
              }
            } else {
              return true;
            }
          }
        }
      }
    }
  }

  consistsOfChange(index: number, option: string) {
    this.projectConsistsOfCheckboxes[index] =
      !this.projectConsistsOfCheckboxes[index];
    if (this.projectConsistsOfCheckboxes[index]) {
      this.projectConsistsOf.push(option);
    } else {
      let matchFound = false;
      for (let i = 0; i < this.projectConsistsOf.length && !matchFound; i++) {
        if (this.projectConsistsOf[i] === option) {
          this.projectConsistsOf.splice(i, 1);
          matchFound = true;
        }
      }
    }
  }

  isVendorHosted() {
    return this.projectConsistsOf.includes(
      "Purchase - Vendor Hosted/Leased, Cloud Computing, or SaaS"
    );
  }

  scopeLength() {
    if (this.projectScopeDescription) {
      return this.projectScopeDescription.length;
    } else {
      return 0;
    }
  }

  softwareProjectLength() {
    if (this.softwareProjectOverview) {
      return this.softwareProjectOverview.length;
    } else {
      return 0;
    }
  }

  deletePatiProject() {
    this.patiProject = "" as any;
    this.displayPatiProject = "";
  }

  deleteITProjectClassification() {
    this.itProjectClassification = "" as any;
  }

  itProjectClassificationValueChosen() {
    for (let i = 0; i < this.itProjectClassificationOptions.length; i++) {
      if (
        this.itProjectClassification.ets_proj_classification ===
        this.itProjectClassificationOptions[i].ets_proj_classification
      ) {
        return true;
      }
    }
    return false;
  }

  hardwareOrSoftwareChosen() {
    for (let i = 0; i < this.hardwareOrSoftwareOptions.length; i++) {
      if (this.hardwareOrSoftware === this.hardwareOrSoftwareOptions[i]) {
        return true;
      }
    }
    return false;
  }

  howSoftwareReleasedChosen() {
    for (let i = 0; i < this.howSoftwareReleasedOptions.length; i++) {
      if (this.howSoftwareReleased === this.howSoftwareReleasedOptions[i]) {
        return true;
      }
    }
    return false;
  }

  deletePrimaryContact() {
    this.primaryContact = "" as any;
    this.primaryContactOptions = [];
  }

  deleteITSponsor() {
    this.itSponsor = "" as any;
    this.itSponsorOptions = [];
  }

  deleteHardwareOrSoftware() {
    this.hardwareOrSoftware = "" as any;
    this.hardwareOrSoftwarePrevious = "" as any;
  }

  deleteHowSoftwareReleased() {
    this.howSoftwareReleased = "" as any;
  }

  saveDraft() {
    if (this.itSparGuid) {
      this.saveItSparData();
    } else {
      this.generateGUID();
    }
  }

  saveItSparData(isRevision?: boolean, deleteDraft?: boolean) {
    let releaseDetailsObject = [];
    for (let i = 0; i < this.releaseDetails.length; i++) {
      releaseDetailsObject.push({
        capabilityDetails: [],
      });
      for (
        let j = 0;
        j < this.releaseDetails[i].capabilityDetails.length;
        j++
      ) {
        releaseDetailsObject[i].capabilityDetails.push({
          releaseNumber:
            this.releaseDetails[i].capabilityDetails[j].releaseNumber,
          releaseDate: this.releaseDetails[i].capabilityDetails[j].releaseDate,
          capability: this.releaseDetails[i].capabilityDetails[j].capabilities,
          description: this.releaseDetails[i].capabilityDetails[j].description,
          capitalCost: this.releaseDetails[i].capabilityDetails[j].capitalCost,
          omCost: this.releaseDetails[i].capabilityDetails[j].omCost,
        });
      }
    }
    let allocationsObject = [];
    for (let i = 0; i < this.allocations.length; i++) {
      allocationsObject.push({
        operatingUnit:
          this.allocations[i].operatingUnit &&
          this.allocations[i].operatingUnit.OPERATING_UNIT
            ? JSON.stringify(this.allocations[i].operatingUnit)
            : "",
        businessUnit: this.allocations[i].businessUnit,
        fundingProjectId: JSON.stringify(this.allocations[i].fundingProjectID),
        function: this.allocations[i].function,
        state: this.allocations[i].state,
        percentage: `${this.allocations[i].percentage}`,
      });
    }
    let buildingsObject = [];
    for (let i = 0; i < this.buildings.length; i++) {
      buildingsObject.push({
        building: JSON.stringify(this.buildings[i].buildingName),
        materialEstimates: [],
      });
      for (let j = 0; j < this.buildings[i].materialEstimates.length; j++) {
        buildingsObject[i].materialEstimates.push({
          cost: this.buildings[i].materialEstimates[j].cost,
          details: this.buildings[i].materialEstimates[j].unitOfPropertyDetails,
          uopDescription:
            this.buildings[i].materialEstimates[j].unitOfPropertyDescription,
          quantity: this.buildings[i].materialEstimates[j].quantity,
          unitCosts: this.buildings[i].materialEstimates[j].unitCosts,
          totalCosts: this.buildings[i].materialEstimates[j].totalCosts,
        });
      }
    }
    let laborDetailsObject = [];
    for (let i = 0; i < this.laborDetails.length; i++) {
      laborDetailsObject.push({
        workTask:
          this.laborDetails[i].workTask &&
          this.laborDetails[i].workTask.workTask
            ? JSON.stringify(this.laborDetails[i].workTask)
            : "",
        workDescription: this.laborDetails[i].workDescription,
        chargeType: this.laborDetails[i].chargeType,
        totalCosts: this.laborDetails[i].totalCosts,
      });
    }
    let contingencyDetailsObject = [];
    for (let i = 0; i < this.contingencyDetails.length; i++) {
      contingencyDetailsObject.push({
        cost: this.contingencyDetails[i].cost,
        description: this.contingencyDetails[i].description,
        totalCosts: this.contingencyDetails[i].totalCosts,
      });
    }
    let filesObject = [];
    for (let i = 0; i < this.files.length; i++) {
      filesObject.push(JSON.stringify(this.files[i]));
    }
    let formStatusValue = "";
    if (isRevision || this.isRevisionIndicator) {
      formStatusValue = "Draft (Revision)";
    } else {
      formStatusValue = "Draft";
    }

    if (deleteDraft) formStatusValue = "Canceled";

    let additionalApproverObject = [];
    for (let i = 0; i < this.additionalApprovers.length; i++) {
      additionalApproverObject.push({
        displayName: this.additionalApprovers[i].displayName,
        givenName: this.additionalApprovers[i].givenName,
        surname: this.additionalApprovers[i].surname,
        mail: this.additionalApprovers[i].mail,
        mailNickname: this.additionalApprovers[i].mailNickname,
        jobTitle: this.additionalApprovers[i].jobTitle,
        doaAmount: this.doaAmounts[i + 1],
      });
    }
    additionalApproverObject = _.orderBy(additionalApproverObject, [
      "doaAmount",
      "surname",
      "givenName",
      "displayName",
    ]);
    let etsPacAssignmentValue = {
      displayName: "",
      givenName: "",
      surname: "",
      mail: "",
      mailNickname: "",
      jobTitle: "",
      doaAmount: "",
    };
    if (isRevision) {
      etsPacAssignmentValue = this.etsPacAssignment;
    }
    let callBody = {
      itSparGuid: this.itSparGuid,
      patiId: JSON.stringify(this.patiProject),
      shortDescription: this.projectName,
      etsProjectClassification: JSON.stringify(this.itProjectClassification),
      estimatedStartDate: this.convertToDateStringFormat(
        this.estimatedStartDate
      ),
      estimatedInServiceDate: this.convertToDateStringFormat(
        this.estimatedInServiceDate
      ),
      overviewAndScope: this.projectScopeDescription,
      primaryContact: {
        displayName: this.primaryContact
          ? this.primaryContact.displayName
          : null,
        givenName: this.primaryContact ? this.primaryContact.givenName : null,
        surname: this.primaryContact ? this.primaryContact.surname : null,
        mail: this.primaryContact ? this.primaryContact.mail : null,
        mailNickname: this.primaryContact
          ? this.primaryContact.mailNickname
          : null,
        jobTitle: this.primaryContact ? this.primaryContact.jobTitle : null,
      },
      itSponsor: {
        displayName: this.itSponsor ? this.itSponsor.displayName : null,
        givenName: this.itSponsor ? this.itSponsor.givenName : null,
        surname: this.itSponsor ? this.itSponsor.surname : null,
        mail: this.itSponsor ? this.itSponsor.mail : null,
        mailNickname: this.itSponsor ? this.itSponsor.mailNickname : null,
        jobTitle: this.itSponsor ? this.itSponsor.jobTitle : null,
        doaAmount:
          this.doaAmounts && this.doaAmounts.length > 0
            ? this.doaAmounts[0]
            : null,
      },
      omOnly: this.omOnly,
      relatedWork: this.relatedWork,
      hardwareOrSoftware: this.hardwareOrSoftware,
      purchasingWarranties: this.purchasingWarranties,
      projectConsistsOf: this.projectConsistsOf,
      expectedLife: this.expectedLife,
      agileEffort: this.agileEffort,
      softwareBeingUsed: this.softwareBeingUsed,
      vendorName: this.vendorName,
      softwareName: this.softwareName,
      inServiceYear: this.inServiceYear,
      softwareProjectOverview: this.softwareProjectOverview,
      dataConversionActivities: this.dataConversionActivities,
      trainingCosts: this.trainingCosts,
      associatedRetirement: this.associatedRetirement,
      softwareToBeRetired: this.softwareToBeRetired,
      retiredInServiceDate: this.retiredInServiceDate,
      maintenanceContracts: this.maintenanceContracts,
      replacingInterfaces: this.replacingInterfaces,
      howSoftwareReleased: this.howSoftwareReleased,
      perpetualLicenses: this.perpetualLicenses,
      contractualRight: this.contractualRight,
      significantPenalty: this.significantPenalty,
      isFeasible: this.isFeasible,
      serviceFees: this.serviceFees,
      useWithoutPaying: this.useWithoutPaying,
      department: JSON.stringify(this.department),
      buildingInfo: JSON.stringify(this.buildingInfo),
      releaseDetails: releaseDetailsObject,
      distributeCost: JSON.stringify(this.distributeCost),
      defaultOption: JSON.stringify(this.defaultOption),
      allocations: allocationsObject,
      buildings: buildingsObject,
      laborDetails: laborDetailsObject,
      contingencyDetails: contingencyDetailsObject,
      additionalComments: this.additionalComments,
      attachments: filesObject,
      approvers: additionalApproverObject,
      formStatus: formStatusValue,
      formActivity: [],
      currentApprover: {
        displayName: "",
        givenName: "",
        surname: "",
        mail: "",
        mailNickname: "",
        jobTitle: "",
        doaAmount: "",
      },
      formOwner: {
        displayName: "",
        givenName: this.firstName,
        surname: this.lastName,
        mail: this.userEmail,
        mailNickname: "",
        jobTitle: "",
        doaAmount: "",
      },
      etsPacAssignment: etsPacAssignmentValue,
      currentAssignment: {
        displayName: "",
        givenName: "",
        surname: "",
        mail: "",
        mailNickname: "",
        jobTitle: "",
        doaAmount: "",
      },
      tables: "",
      effectiveDate: "",
      formNumber: this.formNumber,
      displayRevisionNumber: this.displayRevisionNumber,
      previousCapitalSubtotal: this.previousCapitalSubtotal.toString(),
      previousOMSubtotal: this.previousOMSubtotal.toString(),
      revisionExists: "false",
    };

    this.dataService.saveItSparData(callBody).subscribe(
      (result) => {
        if (result.statusCode !== 200) {
          console.log("error", result);
          let errorType = "";
          let errorDescription = result;
          let dataStructure = {
            errorType: errorType,
            errorDescription: errorDescription,
          };
          this.dialog.open(ErrorDialogComponent, {
            data: dataStructure,
            disableClose: true,
          });
        } else {
          if (deleteDraft) {
            this.clearSessionStorage();
            this.router.navigate(["/dashboard"]);
            return;
          }
          this.formOwner = callBody.formOwner;
          this.draftSaved = "Saved";
          this.draftSavedDate = new Date();
          this.draftSavedDay =
            (this.draftSavedDate.getMonth() > 8
              ? this.draftSavedDate.getMonth() + 1
              : "0" + (this.draftSavedDate.getMonth() + 1)) +
            "/" +
            (this.draftSavedDate.getDate() > 9
              ? this.draftSavedDate.getDate()
              : "0" + this.draftSavedDate.getDate()) +
            "/" +
            this.draftSavedDate.getFullYear();
          this.draftSavedTime = this.formatAMPM(this.draftSavedDate);
          sessionStorage.setItem("draftSaved", this.draftSaved);
          sessionStorage.setItem("draftSavedDay", this.draftSavedDay);
          sessionStorage.setItem("draftSavedTime", this.draftSavedTime);
          this.saveProjectData();
        }
      },
      (error) => {
        console.log("error", error);
        let errorType = "";
        let errorDescription = "";
        if (error.error.message) {
          errorDescription = errorDescription.concat(
            "Save IT SPAR Data Error - ",
            error.error.message
          );
        } else {
          errorDescription = errorDescription.concat("Save IT SPAR Data Error");
        }
        errorType = errorType.concat(error.status, " ", error.statusText);
        let dataStructure = {
          errorType: errorType,
          errorDescription: errorDescription,
        };
        this.dialog.open(ErrorDialogComponent, {
          data: dataStructure,
          disableClose: true,
        });
      }
    );
  }

  generateGUID(isRevision?: boolean) {
    this.dataService.getSparGuid().subscribe(
      (result: any) => {
        this.itSparGuid = result.body.itspar_guid;
        sessionStorage.setItem("itSparGuid", JSON.stringify(this.itSparGuid));
        this.saveItSparData(isRevision);
      },
      (error) => {
        console.log("error", error);
        let errorType = "";
        let errorDescription = "";
        if (error.error.message) {
          errorDescription = errorDescription.concat(
            "Generate IT SPAR GUID Error - ",
            error.error.message
          );
        } else {
          errorDescription = errorDescription.concat(
            "Generate IT SPAR GUID Error"
          );
        }
        errorType = errorType.concat(error.status, " ", error.statusText);
        let dataStructure = {
          errorType: errorType,
          errorDescription: errorDescription,
        };
        this.dialog.open(ErrorDialogComponent, {
          data: dataStructure,
          disableClose: true,
        });
      }
    );
  }

  getItSparData(guid: string, isRevision?: boolean, revisionNumber?: number) {
    let callBody;
    if (revisionNumber !== undefined) {
      callBody = {
        itSparGuid: guid,
        displayRevisionNumber: revisionNumber,
      };
    } else {
      callBody = {
        itSparGuid: guid,
      };
    }
    this.dataService.getItSparData(callBody, "ui").subscribe((result) => {
      this.patiProject = JSON.parse(result.body.patiId);
      if (result.body.patiId) {
        this.patiProject = JSON.parse(result.body.patiId);
        this.displayPatiProject = this.patiProject.Project_ID.concat(
          " - "
        ).concat(this.patiProject.Project_Name);
      } else {
        this.patiProject = null;
        this.displayPatiProject = "";
      }
      if (result.body.shortDescription) {
        this.projectName = result.body.shortDescription;
      } else {
        this.projectName = "";
      }
      if (result.body.etsProjectClassification) {
        this.itProjectClassification = JSON.parse(
          result.body.etsProjectClassification
        );
      } else {
        this.itProjectClassification = null;
      }
      this.itProjectClass = null;
      this.isLighthouseProject = "NO";

      this.formOwner = result.body.formOwner;
      let startDate = result.body.estimatedStartDate;
      if (startDate) {
        this.estimatedStartDate = new Date(startDate);
        this.estimatedStartDate.setDate(this.estimatedStartDate.getDate() + 1);
      } else {
        this.estimatedStartDate = null;
      }
      let inServiceDate = result.body.estimatedInServiceDate;
      if (inServiceDate) {
        this.estimatedInServiceDate = new Date(inServiceDate);
        this.estimatedInServiceDate.setDate(
          this.estimatedInServiceDate.getDate() + 1
        );
      } else {
        this.estimatedInServiceDate = null;
      }
      if (result.body.overviewAndScope) {
        this.projectScopeDescription = result.body.overviewAndScope;
      } else {
        this.projectScopeDescription = "";
      }
      if (result.body.primaryContact && result.body.primaryContact.mail) {
        this.primaryContact = result.body.primaryContact;
        this.primaryContactOptions = [result.body.primaryContact];
      } else {
        this.primaryContact = null;
        this.primaryContactOptions = [];
      }
      if (result.body.itSponsor && result.body.itSponsor.mail) {
        this.itSponsor = result.body.itSponsor;
        this.itSponsorOptions = [result.body.itSponsor];
        this.doaAmounts = [Number(result.body.itSponsor.doaAmount)];
      } else {
        this.itSponsor = null;
        this.itSponsorOptions = [];
        this.doaAmounts = [0];
      }
      if (result.body.omOnly) {
        this.omOnly = result.body.omOnly;
        this.updateHardwareOrSoftwareOptions();
      }
      if (result.body.relatedWork) {
        this.relatedWork = result.body.relatedWork;
      }
      if (result.body.hardwareOrSoftware) {
        this.hardwareOrSoftware = result.body.hardwareOrSoftware;
        this.hardwareOrSoftwarePrevious = result.body.hardwareOrSoftware;
      } else {
        this.hardwareOrSoftware = null;
        this.hardwareOrSoftwarePrevious = null;
      }
      if (result.body.purchasingWarranties) {
        this.purchasingWarranties = result.body.purchasingWarranties;
      } else {
        this.purchasingWarranties = "";
      }
      if (result.body.projectConsistsOf) {
        this.projectConsistsOf = result.body.projectConsistsOf;
      } else {
        this.projectConsistsOf = [];
      }
      this.projectConsistsOfCheckboxes = [];
      for (let i = 0; i < this.projectConsistsOfOptions.length; i++) {
        if (this.projectConsistsOf.includes(this.projectConsistsOfOptions[i])) {
          this.projectConsistsOfCheckboxes.push(true);
        } else {
          this.projectConsistsOfCheckboxes.push(false);
        }
      }
      let expectedLifeCheck = result.body.expectedLife;
      if (expectedLifeCheck) {
        this.expectedLife = Number(expectedLifeCheck);
      } else {
        this.expectedLife = null;
      }
      if (result.body.agileEffort) {
        this.agileEffort = result.body.agileEffort;
      } else {
        this.agileEffort = "";
      }
      if (result.body.softwareBeingUsed) {
        this.softwareBeingUsed = result.body.softwareBeingUsed;
      } else {
        this.softwareBeingUsed = "";
      }
      if (result.body.vendorName) {
        this.vendorName = result.body.vendorName;
      } else {
        this.vendorName = "";
      }
      if (result.body.softwareName) {
        this.softwareName = result.body.softwareName;
      } else {
        this.softwareName = "";
      }
      if (result.body.inServiceYear) {
        this.inServiceYear = result.body.inServiceYear;
      } else {
        this.inServiceYear = "";
      }
      if (result.body.softwareProjectOverview) {
        this.softwareProjectOverview = result.body.softwareProjectOverview;
      } else {
        this.softwareProjectOverview = "";
      }
      if (result.body.dataConversionActivities) {
        this.dataConversionActivities = result.body.dataConversionActivities;
      } else {
        this.dataConversionActivities = "";
      }
      if (result.body.trainingCosts) {
        this.trainingCosts = result.body.trainingCosts;
      } else {
        this.trainingCosts = "";
      }
      if (result.body.associatedRetirement) {
        this.associatedRetirement = result.body.associatedRetirement;
      } else {
        this.associatedRetirement = "";
      }
      if (result.body.softwareToBeRetired) {
        this.softwareToBeRetired = result.body.softwareToBeRetired;
      } else {
        this.softwareToBeRetired = "";
      }
      if (result.body.retiredInServiceDate) {
        this.retiredInServiceDate = result.body.retiredInServiceDate;
      } else {
        this.retiredInServiceDate = "";
      }
      if (result.body.maintenanceContracts) {
        this.maintenanceContracts = result.body.maintenanceContracts;
      } else {
        this.maintenanceContracts = "";
      }
      if (result.body.replacingInterfaces) {
        this.replacingInterfaces = result.body.replacingInterfaces;
      } else {
        this.replacingInterfaces = "";
      }
      if (result.body.howSoftwareReleased) {
        this.howSoftwareReleased = result.body.howSoftwareReleased;
      } else {
        this.howSoftwareReleased = "";
      }
      if (result.body.perpetualLicenses) {
        this.perpetualLicenses = result.body.perpetualLicenses;
      } else {
        this.perpetualLicenses = "";
      }
      if (result.body.contractualRight) {
        this.contractualRight = result.body.contractualRight;
      } else {
        this.contractualRight = "";
      }
      if (result.body.significantPenalty) {
        this.significantPenalty = result.body.significantPenalty;
      } else {
        this.significantPenalty = "";
      }
      if (result.body.isFeasible) {
        this.isFeasible = result.body.isFeasible;
      } else {
        this.isFeasible = "";
      }
      if (result.body.serviceFees) {
        this.serviceFees = result.body.serviceFees;
      } else {
        this.serviceFees = "";
      }
      if (result.body.useWithoutPaying) {
        this.useWithoutPaying = result.body.useWithoutPaying;
      } else {
        this.useWithoutPaying = "";
      }
      let buildingInfoCheck = result.body.buildingInfo;
      if (buildingInfoCheck) {
        this.buildingInfo = JSON.parse(buildingInfoCheck);
      } else {
        this.buildingInfo = null;
      }
      let releaseDetailsCheck = result.body.releaseDetails;
      if (releaseDetailsCheck) {
        this.releaseDetails = result.body.releaseDetails;
        for (let i = 0; i < this.releaseDetails.length; i++) {
          for (
            let j = 0;
            j < this.releaseDetails[i].capabilityDetails.length;
            j++
          ) {
            this.releaseDetails[i].capabilityDetails[j].capabilities =
              result.body.releaseDetails[i].capabilityDetails[j].capability;
          }
        }
      } else {
        this.releaseDetails = [new ReleaseDetail()];
      }
      let distributeCostCheck = result.body.distributeCost;
      if (distributeCostCheck) {
        this.distributeCost = JSON.parse(result.body.distributeCost);
      } else {
        this.distributeCost = "Manually";
      }
      let defaultOptionCheck = result.body.defaultOption;
      if (defaultOptionCheck) {
        this.defaultOption = JSON.parse(result.body.defaultOption);
      } else {
        this.defaultOption = "";
      }
      let allocationsCheck = result.body.allocations;
      if (allocationsCheck) {
        this.allocations = allocationsCheck;
        for (let i = 0; i < this.allocations.length; i++) {
          this.allocations[i].fundingProjectID = result.body.allocations[i]
            .fundingProjectId
            ? JSON.parse(result.body.allocations[i].fundingProjectId)
            : null;
          this.allocations[i].operatingUnit = parseOperatingUnit(
            result.body.allocations[i].operatingUnit,
            result.body.allocations[i].businessUnit
          );
          if (
            this.allocations[i].operatingUnit &&
            this.allocations[i].operatingUnit.OPERATING_UNIT
          ) {
            this.allocations[i].displayOperatingUnit = this.allocations[
              i
            ].operatingUnit.OPERATING_UNIT.concat(" - ").concat(
              this.allocations[i].operatingUnit.DESCR
            );
          } else {
            this.allocations[i].displayOperatingUnit = "";
          }
          this.allocations[i].percentage = Number(
            result.body.allocations[i].percentage
          );
          let matchFound = false;
          for (
            let j = 0;
            j < this.buForStateOptions.length && !matchFound;
            j++
          ) {
            if (
              this.allocations[i].operatingUnit &&
              this.allocations[i].operatingUnit.BUSINESS_UNIT ===
                this.buForStateOptions[j]
            ) {
              matchFound = true;
              this.allocations[i].stateOptions = ["NC", "SC", "NC & SC"];
            }
          }
          if (!matchFound) {
            this.allocations[i].stateOptions = [];
          }
        }
      } else {
        this.allocations = [new Allocation()];
      }
      let departmentCheck = result.body.department;
      if (departmentCheck) {
        this.department = JSON.parse(departmentCheck);
      } else {
        this.department = null;
      }
      let buildingsCheck = result.body.buildings;
      if (buildingsCheck && buildingsCheck.length > 0) {
        this.buildings = buildingsCheck;
        for (let i = 0; i < this.buildings.length; i++) {
          this.buildings[i].buildingName = result.body.buildings[i].building
            ? JSON.parse(result.body.buildings[i].building)
            : "";
          for (let j = 0; j < this.buildings[i].materialEstimates.length; j++) {
            this.buildings[i].materialEstimates[j].unitOfPropertyDescription =
              result.body.buildings[i].materialEstimates[j].uopDescription;
            this.buildings[i].materialEstimates[j].unitOfPropertyDetails =
              result.body.buildings[i].materialEstimates[j].details;
          }
        }
      } else {
        this.buildings = [new Building()];
      }
      let laborDetailsCheck = result.body.laborDetails;
      if (laborDetailsCheck) {
        this.laborDetails = laborDetailsCheck;
        for (let i = 0; i < this.laborDetails.length; i++) {
          if (result.body.laborDetails[i].workTask) {
            this.laborDetails[i].workTask = JSON.parse(
              result.body.laborDetails[i].workTask
            );
          } else {
            this.laborDetails[i].workTask = "";
          }
        }
      } else {
        this.laborDetails = [new LaborDetail()];
      }
      let contingencyDetailsCheck = result.body.contingencyDetails;
      if (contingencyDetailsCheck) {
        this.contingencyDetails = contingencyDetailsCheck;
      } else {
        this.contingencyDetails = [new ContingencyDetail()];
      }
      if (result.body.additionalComments) {
        this.additionalComments = result.body.additionalComments;
      } else {
        this.additionalComments = "";
      }
      let attachments = result.body.attachments;
      this.files = [];
      if (attachments) {
        for (let i = 0; i < attachments.length; i++) {
          this.files.push(JSON.parse(attachments[i]));
        }
      }
      let approversCheck = result.body.approvers;
      if (approversCheck) {
        this.additionalApprovers = result.body.approvers;
        this.approverList = approversCheck;
        for (let i = 0; i < this.approverList.length; i++) {
          this.doaAmounts.push(Number(this.approverList[i].doaAmount));
        }
      } else {
        this.approverList = [];
      }
      let formStatusCheck = result.body.formStatus;
      if (formStatusCheck) {
        this.formStatus = result.body.formStatus;
        if (this.formStatus === "Draft (Revision)") {
          this.isRevisionIndicator = true;
        }
      } else {
        this.formStatus = "";
      }
      this.displayRevisionNumber = Number(result.body.displayRevisionNumber);
      if (isRevision) {
        this.etsPacAssignment = result.body.etsPacAssignment;
      }
      let formNumberCheck = result.body.formNumber;
      if (formNumberCheck) {
        this.formNumber = result.body.formNumber;
      } else {
        this.formNumber = "";
      }
      if (!isRevision) {
        if (result.body.previousCapitalSubtotal) {
          this.previousCapitalSubtotal = Number(
            result.body.previousCapitalSubtotal
          );
        } else {
          this.previousCapitalSubtotal = 0;
        }
        if (result.body.previousOMSubtotal) {
          this.previousOMSubtotal = Number(result.body.previousOMSubtotal);
        } else {
          this.previousOMSubtotal = 0;
        }
      }
      this.validatingPati = false;
      this.patiValidated = false;
      this.invalidPati = false;
      this.generateTodaysDate();
      this.filteredPrimaryContactOptions =
        this.primaryContactControl.valueChanges.pipe(
          startWith<string | GraphUser[]>(""),
          map((value) => (typeof value === "string" ? value : this.lastFilter)),
          map((filter) => this.primaryContactFilter(filter))
        );
      this.filteredItSponsorOptions = this.itSponsorControl.valueChanges.pipe(
        startWith<string | GraphUser[]>(""),
        map((value) => (typeof value === "string" ? value : this.lastFilter)),
        map((filter) => this.itSponsorFilter(filter))
      );
      this.sparDataLoaded = true;
      if (isRevision) {
        // Increment the revision number locally only. Do NOT persist to DynamoDB yet.
        // The draft revision row will only be created when the user explicitly saves.
        this.displayRevisionNumber = Number(this.displayRevisionNumber) + 1;

        /*this.itSparGuid = '';
        this.generateGUID(isRevision);*/
        // Removed: this.saveItSparData(isRevision);
        // User must now explicitly save when they want to persist the draft.
      }
    });
  }

  formatAMPM(date) {
    var hours = date.getHours();
    var minutes = date.getMinutes();
    var ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    minutes = minutes < 10 ? "0" + minutes : minutes;
    var strTime = hours + ":" + minutes + " " + ampm;
    return strTime;
  }

  loadBUForStateFile() {
    return this.http.get("assets/bu-for-state.json").pipe(tap(
      (result) => {
        this.buForStateOptions = result as string[];
      },
      (error) => {
        console.log("error", error);
        let errorType = "";
        let errorDescription = "";
        if (error.error.message) {
          errorDescription = errorDescription.concat(
            "Load BU For State Error - ",
            error.error.message
          );
        } else {
          errorDescription = errorDescription.concat("Load BU For State Error");
        }
        errorType = errorType.concat(error.status, " ", error.statusText);
        let dataStructure = {
          errorType: errorType,
          errorDescription: errorDescription,
        };
        this.dialog.open(ErrorDialogComponent, {
          data: dataStructure,
          disableClose: true,
        });
      }
    ));
  }
  compareItProjectClassification(o1: any, o2: any): boolean {
    if (!o1 || !o2) {
      return false;
    }
    return o1.it_proj_classification === o2.it_proj_classification;
  }

  isFormOwner() {
    return (
      this.userEmail &&
      this.formOwner &&
      this.formOwner.mail &&
      this.userEmail.toLowerCase() === this.formOwner.mail.toLowerCase()
    );
  }

  isPACUser() {
    return (
      this.userRoles.includes("MMAPS_ETS_PAC_Users")
    );
  }

  showDelete() {
    if (this.isPACUser()) return true;
    if (!this.formOwner) return true;
    return this.isFormOwner();
  }

  openDeleteConfirmationDialog(): void {
    const dialogRef = this.dialog.open(ConfirmDeleteFormDialogComponent, {
      width: "400px",
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === "delete") {
        this.deleteForm();
      }
    });
  }

  deleteForm(): void {
    this.saveItSparData(false, true);
  }
}
